const text = (...values) => values.find((value) => String(value ?? '').trim())?.toString().trim() || '';
const token = (value) => text(value).toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');

export const isCustomerPickup = (method) => {
  const normalized = token(method).replace(/_+/g, '_');
  return ['customer_pickup', 'customer_pick_up', 'pickup', 'pick_up'].includes(normalized);
};

const decoratorShipping = (po) => po?.decoratorFormData || po?.supplierInfo?.decoratorFormData ||
  po?.decoratorShipping || po?.decoration?.shipping || po?.supplierInfo?.decoration?.shipping || {};

const clientFrom = (po, order, customer) => ({
  type: 'client',
  id: customer?.id ?? customer?.customerId ?? order?.customerId ??
    po?.routeDestination?.customerId ?? po?.supplierInfo?.routeDestination?.customerId ??
    po?.customerId ?? po?.supplierInfo?.customerId ?? null,
  name: text(customer?.businessName, customer?.companyName, customer?.name,
    order?.customer?.businessName, order?.customer?.companyName, order?.customerName, order?.CustomerName,
    po?.routeDestination?.name, po?.supplierInfo?.routeDestination?.name,
    po?.routeDestinationName, po?.supplierInfo?.routeDestinationName,
    po?.customer?.businessName, po?.customerName,
    po?.supplierInfo?.customerName, po?.salesOrder?.customerName,
    // The physical address is deliberately excluded: pickup locations are not client identities.
    'Client'),
});

const decoratorFrom = (po, decorator) => ({
  type: 'decorator',
  id: decorator?.id ?? decorator?.decoratorId ?? po?.decoratorId ?? po?.supplierInfo?.decoratorId ?? null,
  name: text(decorator?.businessName, decorator?.companyName, decorator?.name,
    po?.routeOriginType === 'decorator' ? po?.routeOriginName : '',
    po?.decoratorName, po?.supplierInfo?.decoratorName, po?.supplierInfo?.decorator?.name,
    po?.supplierInfo?.supplierName),
});

const supplierFrom = (po) => ({
  type: 'supplier',
  id: po?.supplierId ?? po?.supplierInfo?.supplierId ?? null,
  name: text(po?.routeOriginType === 'supplier' ? po?.routeOriginName : '',
    po?.supplierInfo?.supplierName, po?.supplierName),
});

const routeSays = (po, destination) => {
  const explicit = token(po?.routeLegType || po?.legType || po?.supplierInfo?.routeLegType);
  if (explicit) return explicit.endsWith(`_to_${destination}`);
  const destinationType = token(po?.routeDestinationType || po?.supplierInfo?.routeDestinationType);
  if (destinationType) return destinationType === destination;
  const summary = token(po?.routeSummary || po?.supplierInfo?.routeSummary);
  return summary.endsWith(`_to_${destination}`);
};

const hasDecorationLink = (po) => {
  const links = po?.linkedPurchaseOrders || po?.supplierInfo?.linkedPurchaseOrders || [];
  return Array.isArray(links) && links.some((link) => token(link?.relationship) === 'goods_to_decoration');
};

const addressById = (locations, id) => {
  if (id == null || !Array.isArray(locations)) return null;
  return locations.find((location) => String(location?.id ?? location?.addressId) === String(id)) || null;
};

export const resolveInternalPickupAddress = ({ purchaseOrder: po = {}, decorator = {}, internalShippingSettings = {} }) => {
  const shipping = decoratorShipping(po);
  const selectedId = shipping?.pickupLocationId ?? po?.pickupLocationId ?? po?.physicalAddressId;
  const locations = internalShippingSettings?.pickupLocations || internalShippingSettings?.locations || [];
  return addressById(locations, selectedId) ||
    internalShippingSettings?.pickupAddress || internalShippingSettings?.address ||
    po?.internalPickupAddress || po?.supplierInfo?.internalPickupAddress ||
    decorator?.internalAddress || decorator?.address ||
    po?.instance?.businessAddress || po?.instanceBusinessAddress || po?.companyAddress || null;
};

/** Resolve party identities before selecting the address printed on a generated PO. */
export const resolvePurchaseOrderRoute = ({
  purchaseOrder: po = {}, order = {}, customer = {}, decorator = {},
  shippingDestination = null, internalShippingSettings = {},
} = {}) => {
  const partyType = token(po?.partyType || po?.poPartyType || po?.routeOriginType);
  const explicitLeg = token(po?.routeLegType || po?.legType);
  const supplierInfoRole = token(po?.supplierInfo?.shippingRole || po?.shippingRole);
  const decoratorParty = partyType === 'decorator' || supplierInfoRole === 'decorator' ||
    po?.decoratorId != null || po?.supplierInfo?.decoratorId != null ||
    explicitLeg.startsWith('decorator_to_') ||
    (routeSays(po, 'client') && token(po?.supplierInfo?.routeSummary).startsWith('decorator_to_')) ||
    (!partyType && token(po?.type || po?.supplierInfo?.type).includes('decorator'));
  const client = clientFrom(po, order, customer);
  const decoration = decoratorFrom(po, decorator);
  const supplier = supplierFrom(po);
  const destinationIsDecorator = routeSays(po, 'decorator') || hasDecorationLink(po);
  const hasExplicitDestination = Boolean(token(po?.routeDestinationType || po?.supplierInfo?.routeDestinationType));
  const destinationIsClient = routeSays(po, 'client') ||
    (!destinationIsDecorator && decoratorParty && !explicitLeg && !hasExplicitDestination);

  let legType = explicitLeg || 'unknown';
  let origin = decoratorParty ? decoration : supplier;
  let destination = { type: 'unknown', id: null, name: '' };
  if (decoratorParty && destinationIsClient) {
    legType = 'decorator_to_client';
    destination = client;
  } else if (!decoratorParty && destinationIsDecorator) {
    legType = 'supplier_to_decorator';
    const legacyDestination = shippingDestination || po?.shipTo || po?.supplierInfo?.shipTo || po?.shippingAddress;
    destination = {
      ...decoration,
      name: text(
        po?.routeDestinationName,
        decoration.name && decoration.name !== supplier.name ? decoration.name : '',
        legacyDestination?.name,
        legacyDestination?.company,
        decoration.name
      ),
    };
  } else if (!decoratorParty && (destinationIsClient || client.name)) {
    legType = 'supplier_to_client';
    destination = client;
  } else if (explicitLeg.includes('decorator_to_decorator') || explicitLeg.includes('internal_transfer')) {
    destination = decoration;
  }

  // Snapshots win over derived names, without conflating an address company with a route party.
  origin = { ...origin, name: text(po?.routeOrigin?.name, po?.supplierInfo?.routeOrigin?.name, po?.routeOriginName, origin.name) };
  destination = { ...destination, name: text(
    po?.routeDestination?.name, po?.supplierInfo?.routeDestination?.name,
    po?.routeDestinationName, po?.supplierInfo?.routeDestinationName, destination.name,
  ) };
  const shipping = decoratorShipping(po);
  const method = text(po?.fulfillmentMethod, shipping?.shippingMethodLabel, shipping?.shippingMethod,
    po?.supplierInfo?.shippingMethodLabel, po?.supplierInfo?.shippingMethod,
    po?.shippingMethodLabel, po?.shippingMethod);
  const pickup = isCustomerPickup(method);
  const physicalAddress = pickup
    ? resolveInternalPickupAddress({ purchaseOrder: po, decorator, internalShippingSettings })
    : (shippingDestination || po?.shipTo || po?.supplierInfo?.shipTo || po?.shippingAddress || null);

  return {
    legType,
    origin,
    destination,
    fulfillment: {
      method: pickup ? 'customer_pickup' : method,
      physicalAddressType: pickup ? 'internal_pickup_location' :
        (destination.type === 'decorator' ? 'decorator' : 'client_shipping'),
      physicalAddress,
    },
  };
};
